/**
 * Bundle 108 fold-in - read-only /admin/_debug/campaigns diagnostic.
 *
 * Route: /admin/_debug/campaigns  (super_admin only)
 *
 * Answers the question "why do I see N rows on Drift Scan for the
 * same event?" by grouping every `event_survey_campaigns` row by
 * `event_id` and rendering the per-campaign fingerprint stats
 * side by side. Events with more than one campaign row are flagged
 * as an invariant violation. Per-event "Preview backfill" button
 * proxies the existing dry-run backfill endpoint.
 *
 * READ ONLY - no writes. No PII on the wire.
 *
 * No em/en dashes, no lint disable comments, no photo/image alt.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertTriangle, RefreshCcw, ChevronDown, ChevronRight,
  Beaker,
} from 'lucide-react';
import api from '@/utils/api';
import { toast } from 'sonner';
import IncidentActionsPanel from './components/IncidentActionsPanel';

const _short = (v) => (v ? String(v).slice(0, 8) : '');

const AdminDebugCampaignsPage = () => {
  const [loading, setLoading] = useState(true);
  const [payload, setPayload] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [dryRun, setDryRun] = useState({});
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/admin/_debug/campaigns');
      setPayload(data);
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Could not load debug payload.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runDryRun = async (eventId) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/backfill-bundle107-reissue',
        { event_id: eventId, dry_run: true },
      );
      setDryRun((s) => ({ ...s, [eventId]: data }));
      setDialog({ eventId, kind: 'backfill', data });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Dry-run failed.',
      );
    } finally { setBusy(false); }
  };

  const runConsolidateDryRun = async (eventId) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/consolidate-campaigns',
        { event_id: eventId, dry_run: true },
      );
      setDialog({ eventId, kind: 'consolidate', data });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Consolidate dry-run failed.',
      );
    } finally { setBusy(false); }
  };

  const applyConsolidate = async (eventId) => {
    if (!window.confirm(
      'Apply consolidation? Duplicate campaign rows will be archived and their invitations, responses, and email logs will be re-pointed to the surviving campaign. This is idempotent but not reversible without a DB restore.'
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/consolidate-campaigns',
        { event_id: eventId, dry_run: false },
      );
      toast.success(
        `Archived ${data.archived?.length || 0}, re-pointed ${(data.repointed?.invitations || 0) + (data.repointed?.responses || 0) + (data.repointed?.email_logs || 0)} rows.`,
      );
      setDialog(null);
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Consolidate failed.',
      );
    } finally { setBusy(false); }
  };

  const runDedupeDryRun = async (eventId, campaignId) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/dedupe-invitations',
        { event_id: eventId, campaign_id: campaignId, dry_run: true },
      );
      setDialog({ eventId, campaignId, kind: 'dedupe', data });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Dedupe dry-run failed.',
      );
    } finally { setBusy(false); }
  };

  const applyDedupe = async (eventId, campaignId) => {
    if (!window.confirm(
      'Apply dedupe? Duplicate invitations and responses for the same registration_id will be stamped as deduplicated + invalidated. Idempotent. Send Live will only count the surviving row per recipient.'
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/dedupe-invitations',
        { event_id: eventId, campaign_id: campaignId, dry_run: false },
      );
      toast.success(
        `Deduped ${data.deduped_invitation_count || 0} invitations, ${data.deduped_response_count || 0} responses. Active now: ${data.final_active_inv_count}.`,
      );
      setDialog(null);
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Dedupe failed.',
      );
    } finally { setBusy(false); }
  };

  const runResetStaleDryRun = async (eventId) => {
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/reset-stale-invitations',
        { event_id: eventId, dry_run: true },
      );
      setDialog({ eventId, kind: 'reset_stale', data });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Reset stale dry-run failed.',
      );
    } finally { setBusy(false); }
  };

  const applyResetStale = async (eventId) => {
    if (!window.confirm(
      'Apply reset? Every non-invalidated invitation on the active campaign whose registration_id is NOT in the last 90 days of reissue email_logs will be invalidated. Reissued recipients keep their valid tokens. Idempotent.'
    )) return;
    setBusy(true);
    try {
      const { data } = await api.post(
        '/admin/incident/reset-stale-invitations',
        { event_id: eventId, dry_run: false },
      );
      toast.success(
        `Invalidated ${data.invalidated || 0} stale invitations. ${data.reissued_count || 0} reissued recipients preserved.`,
      );
      setDialog(null);
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || 'Reset stale failed.',
      );
    } finally { setBusy(false); }
  };

  const violations = useMemo(
    () => (payload?.events || []).filter((r) => r.invariant_violation),
    [payload],
  );
  const backfillEligible = useMemo(
    () => (payload?.events || []).filter((r) => r.backfill_eligible),
    [payload],
  );

  return (
    <div className="p-8 max-w-7xl space-y-6"
          data-testid="admin-debug-campaigns-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Beaker className="h-5 w-5 text-primary-900" />
            <h1 className="font-display text-3xl font-bold text-primary-900">
              Campaign debug
            </h1>
          </div>
          <p className="text-neutral-600 text-sm max-w-2xl mt-2">
            Read-only. Shows every row in
            {' '}<code>event_survey_campaigns</code> grouped by
            {' '}<code>event_id</code> so you can see when the
            invariant of one campaign per event is violated.
            Rows flagged red are the ones to investigate.
          </p>
        </div>
        <Button variant="outline" onClick={load}
                  data-testid="debug-refresh-btn"
                  disabled={loading || busy}>
          <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <IncidentActionsPanel />

      <div className="grid grid-cols-4 gap-3">
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-neutral-500">Events</div>
          <div className="font-display text-3xl font-bold text-primary-900"
                data-testid="debug-total-events">
            {payload?.total_events ?? '.'}
          </div>
        </CardContent></Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-neutral-500">Campaign rows</div>
          <div className="font-display text-3xl font-bold text-primary-900"
                data-testid="debug-total-campaigns">
            {payload?.total_campaigns ?? '.'}
          </div>
        </CardContent></Card>
        <Card className={
          (payload?.invariant_violations ?? 0) > 0
            ? 'border-red-300 bg-red-50' : ''
        }>
          <CardContent className="p-4">
            <div className="text-xs uppercase text-neutral-500">
              Invariant violations
            </div>
            <div className={`font-display text-3xl font-bold ${
              (payload?.invariant_violations ?? 0) > 0
                ? 'text-red-800' : 'text-primary-900'
            }`}
              data-testid="debug-violations-count">
              {payload?.invariant_violations ?? '.'}
            </div>
          </CardContent>
        </Card>
        <Card><CardContent className="p-4">
          <div className="text-xs uppercase text-neutral-500">
            Backfill-eligible events
          </div>
          <div className="font-display text-3xl font-bold text-primary-900"
                data-testid="debug-backfill-eligible-count">
            {payload?.backfill_eligible_events ?? '.'}
          </div>
        </CardContent></Card>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm">Loading.</div>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm relative"
                    data-testid="debug-campaigns-table">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase">
                <tr>
                  <th className="text-left p-3 w-8"></th>
                  <th className="text-left p-3">Event</th>
                  <th className="text-left p-3">Date</th>
                  <th className="text-right p-3" title="Total campaign rows (active + archived)">Camps</th>
                  <th className="text-right p-3" title="survey_reissue / legacy_survey_apology counts">Reissue</th>
                  <th className="text-right p-3" title="Same-id backfillable email_logs">Backfill</th>
                  <th className="text-left p-3" title="Recommended backfill target campaign">Target</th>
                  <th className="text-right p-3 sticky right-0 bg-neutral-50 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(payload?.events || []).map((r) => {
                  const isOpen = !!expanded[r.event_id];
                  const rowClass = r.invariant_violation
                    ? 'bg-red-50 border-b border-red-100'
                    : 'border-b border-neutral-100';
                  return (
                    <React.Fragment key={r.event_id}>
                      <tr className={rowClass}
                          data-testid={`debug-event-row-${_short(r.event_id)}`}>
                        <td className="p-3">
                          <button type="button"
                                  onClick={() => setExpanded((s) => ({
                                    ...s, [r.event_id]: !s[r.event_id],
                                  }))}
                                  className="text-neutral-500 hover:text-primary-900"
                                  data-testid={`debug-expand-${_short(r.event_id)}`}>
                            {isOpen ? <ChevronDown className="h-4 w-4" />
                              : <ChevronRight className="h-4 w-4" />}
                          </button>
                        </td>
                        <td className="p-3">
                          <div className="font-medium text-primary-900">
                            {r.event_title || <em className="text-neutral-400">
                              (untitled)</em>}
                            {r.is_deleted ? (
                              <Badge variant="outline"
                                      className="ml-2 border-red-300 text-red-800 text-[10px]">
                                deleted
                              </Badge>
                            ) : null}
                            {r.is_archived ? (
                              <Badge variant="outline"
                                      className="ml-2 border-neutral-300 text-neutral-700 text-[10px]">
                                archived
                              </Badge>
                            ) : null}
                          </div>
                          <div className="text-xs text-neutral-500 font-mono">
                            {r.event_id}
                          </div>
                        </td>
                        <td className="p-3 text-neutral-700">
                          {(r.event_date || '').slice(0, 10)}
                        </td>
                        <td className="p-3 text-right">
                          {r.invariant_violation ? (
                            <span className="inline-flex items-center gap-1 font-semibold text-red-800">
                              <AlertTriangle className="h-3 w-3" />
                              {r.campaign_count}
                            </span>
                          ) : (
                            <span className="text-neutral-700">
                              {r.campaign_count}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-right text-neutral-700">
                          <div>{r.reissue_logs?.total ?? 0}</div>
                          {r.reissue_logs?.by_template ? (
                            <div className="text-[10px] text-neutral-500 mt-0.5">
                              sr={r.reissue_logs.by_template.survey_reissue}
                              {' '}la={r.reissue_logs.by_template.legacy_survey_apology}
                            </div>
                          ) : null}
                        </td>
                        <td className="p-3 text-right">
                          <span className={
                            (r.reissue_logs?.same_id_backfillable ?? 0) > 0
                              ? 'font-semibold text-primary-900'
                              : 'text-neutral-500'
                          }>
                            {r.reissue_logs?.same_id_backfillable ?? 0}
                          </span>
                        </td>
                        <td className="p-3 text-xs">
                          {r.recommended_backfill_campaign_id ? (
                            <div>
                              <span className="font-mono text-primary-900">
                                {_short(r.recommended_backfill_campaign_id)}
                              </span>
                              <div className="text-neutral-500 mt-0.5 text-[11px]">
                                {r.recommended_backfill_reason}
                              </div>
                            </div>
                          ) : (
                            <span className="text-neutral-400">.</span>
                          )}
                        </td>
                        <td className={`p-3 text-right sticky right-0 shadow-[-6px_0_6px_-6px_rgba(0,0,0,0.15)] ${
                          r.invariant_violation ? 'bg-red-50' : 'bg-white'
                        }`}>
                          <div className="flex flex-col gap-1 items-end">
                            {r.invariant_violation ? (
                              <Button size="sm" variant="outline"
                                        onClick={() => runConsolidateDryRun(r.event_id)}
                                        disabled={busy}
                                        data-testid={`debug-consolidate-btn-${_short(r.event_id)}`}>
                                Consolidate campaigns
                              </Button>
                            ) : null}
                            {r.recommended_backfill_campaign_id ? (
                              <Button size="sm" variant="outline"
                                        onClick={() => runDedupeDryRun(r.event_id, r.recommended_backfill_campaign_id)}
                                        disabled={busy}
                                        data-testid={`debug-dedupe-btn-${_short(r.event_id)}`}>
                                Dedupe invitations
                              </Button>
                            ) : null}
                            <Button size="sm" variant="outline"
                                      onClick={() => runResetStaleDryRun(r.event_id)}
                                      disabled={busy}
                                      data-testid={`debug-reset-stale-btn-${_short(r.event_id)}`}>
                              Reset stale invitations
                            </Button>
                            <Button size="sm" variant="outline"
                                      onClick={() => runDryRun(r.event_id)}
                                      disabled={busy || !r.backfill_eligible}
                                      data-testid={`debug-preview-backfill-${_short(r.event_id)}`}>
                              Preview backfill
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {isOpen ? (
                        <tr className="bg-neutral-50">
                          <td colSpan={8} className="p-4"
                              data-testid={`debug-expanded-${_short(r.event_id)}`}>
                            <div className="text-xs uppercase text-neutral-500 mb-2">
                              Campaign rows for {r.event_title}
                            </div>
                            <table className="w-full text-xs bg-white border border-neutral-200 rounded-md">
                              <thead className="text-neutral-600">
                                <tr>
                                  <th className="text-left p-2">campaign_id</th>
                                  <th className="text-left p-2">status</th>
                                  <th className="text-left p-2">created_at</th>
                                  <th className="text-left p-2">created_by</th>
                                  <th className="text-left p-2">q</th>
                                  <th className="text-left p-2">step_titles</th>
                                  <th className="text-left p-2">bundle</th>
                                  <th className="text-left p-2">version</th>
                                  <th className="text-right p-2">sent</th>
                                  <th className="text-right p-2">outstanding</th>
                                  <th className="text-right p-2">invalidated (inv)</th>
                                  <th className="text-right p-2">submitted</th>
                                  <th className="text-right p-2">reissue chain</th>
                                  <th className="text-right p-2">reissue logs (this camp)</th>
                                  <th className="text-left p-2">paused_at</th>
                                  <th className="text-left p-2">camp invalidated_at</th>
                                </tr>
                              </thead>
                              <tbody>
                                {(r.campaigns || []).map((c) => (
                                  <tr key={c.campaign_id}
                                      className="border-t border-neutral-100"
                                      data-testid={`debug-campaign-row-${c.campaign_id_short}`}>
                                    <td className="p-2 font-mono">
                                      {c.campaign_id_short}
                                    </td>
                                    <td className="p-2">
                                      <Badge variant="outline"
                                              className="text-[10px]">
                                        {c.status}
                                      </Badge>
                                    </td>
                                    <td className="p-2 text-neutral-500 whitespace-nowrap">
                                      {c.created_at ? c.created_at.slice(0, 19) : '.'}
                                    </td>
                                    <td className="p-2 font-mono text-[10px] text-neutral-600">
                                      {c.created_by ? String(c.created_by).slice(0, 8) : '.'}
                                    </td>
                                    <td className="p-2">
                                      {c.attendee_question_count}
                                    </td>
                                    <td className="p-2">
                                      {c.step_titles_count}
                                    </td>
                                    <td className="p-2">
                                      {c.snapshot_bundle || '.'}
                                    </td>
                                    <td className="p-2">
                                      {c.snapshot_version || '.'}
                                    </td>
                                    <td className="p-2 text-right">{c.sent}</td>
                                    <td className="p-2 text-right">{c.outstanding}</td>
                                    <td className="p-2 text-right">{c.invalidated}</td>
                                    <td className="p-2 text-right">{c.submitted}</td>
                                    <td className="p-2 text-right">{c.reissue_chain_rows}</td>
                                    <td className="p-2 text-right">
                                      {c.reissue_email_logs_for_campaign}
                                    </td>
                                    <td className="p-2 text-neutral-500 whitespace-nowrap">
                                      {c.paused_at ? c.paused_at.slice(0, 19) : '.'}
                                    </td>
                                    <td className="p-2 text-neutral-500 whitespace-nowrap">
                                      {c.invalidated_at ? c.invalidated_at.slice(0, 19) : '.'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                            <div className="mt-3 text-xs text-neutral-600">
                              <div className="flex items-center gap-1">
                                <span>Reissue log rollup for this event:</span>
                                <span title="Neither reissue path currently stamps meta.campaign_id, so per-campaign reissue-log counts may show 0 even when the event total is non-zero."
                                        className="cursor-help text-neutral-400 border border-neutral-300 rounded-full w-4 h-4 inline-flex items-center justify-center text-[10px] font-mono">
                                  i
                                </span>
                              </div>
                              <div className="mt-1">
                                {' '}survey_reissue ={' '}
                                <b>{r.reissue_logs?.by_template?.survey_reissue ?? 0}</b>,
                                {' '}legacy_survey_apology ={' '}
                                <b>{r.reissue_logs?.by_template?.legacy_survey_apology ?? 0}</b>,
                                {' '}same-id backfillable ={' '}
                                <b>{r.reissue_logs?.same_id_backfillable ?? 0}</b>,
                                {' '}distinct-id already healed ={' '}
                                <b>{r.reissue_logs?.distinct_id_already_healed ?? 0}</b>,
                                {' '}missing meta ={' '}
                                <b>{r.reissue_logs?.missing_meta ?? 0}</b>,
                                {' '}unique registrations ={' '}
                                <b>{r.reissue_logs?.unique_registrations ?? 0}</b>.
                              </div>
                            </div>
                            <div className="mt-3 border-t border-neutral-200 pt-3"
                                  data-testid={`debug-template-histogram-${_short(r.event_id)}`}>
                              <div className="text-xs uppercase text-neutral-500 mb-1">
                                All email templates seen for this event
                                {' '}(Bundle 110 discovery)
                              </div>
                              <div className="text-xs text-neutral-600 mb-1">
                                Registrations scanned:{' '}
                                <b>{r.template_histogram?.registrations_scanned ?? 0}</b>
                                {', logs matched: '}
                                <b>{r.template_histogram?.logs_matched ?? 0}</b>.
                              </div>
                              {Object.keys(r.template_histogram?.by_template || {}).length ? (
                                <table className="text-xs w-full max-w-md">
                                  <thead className="text-neutral-500">
                                    <tr>
                                      <th className="text-left p-1">template_id</th>
                                      <th className="text-right p-1">count</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {Object.entries(r.template_histogram.by_template)
                                      .sort((a, b) => b[1] - a[1])
                                      .map(([k, v]) => (
                                        <tr key={k} className="border-t border-neutral-100">
                                          <td className="p-1 font-mono">{k}</td>
                                          <td className="p-1 text-right">{v}</td>
                                        </tr>
                                      ))}
                                  </tbody>
                                </table>
                              ) : (
                                <div className="text-xs text-neutral-500">
                                  No email logs matched any registration_id for this event.
                                </div>
                              )}
                              {(r.template_histogram?.notes || []).map((n, i) => (
                                <div key={i} className="text-[11px] text-yellow-800 mt-1">
                                  {n}
                                </div>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {(violations.length > 0 || backfillEligible.length > 0) ? (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardContent className="p-4 text-sm text-yellow-900">
            <div className="font-semibold mb-1 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Reading this table
            </div>
            <ol className="list-decimal ml-5 space-y-1">
              <li>
                Rows shaded red have more than one row in
                {' '}<code>event_survey_campaigns</code>. That is the
                {' '}<b>invariant violation</b> the Drift Scan is
                reflecting. No fix is proposed yet - screenshot this
                page back to the agent for the safe repair plan.
              </li>
              <li>
                The <b>Recommended target</b> column names the single
                {' '}<code>campaign_id</code> where Bundle 107 reissue
                emails were sent. That is the campaign whose data
                should be preserved.
              </li>
              <li>
                Click <b>Preview backfill</b> to see (dry-run) how
                many invitation rows the Bundle 108 endpoint would
                mint for that event. Nothing is written.
              </li>
              <li>
                No action is executed from this page. Repair only
                happens after you approve.
              </li>
            </ol>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-2xl"
                        data-testid="debug-dryrun-dialog">
          <DialogHeader>
            <DialogTitle>
              {dialog?.kind === 'consolidate'
                ? 'Consolidate campaigns (dry-run)'
                : dialog?.kind === 'dedupe'
                ? 'Dedupe invitations (dry-run)'
                : dialog?.kind === 'reset_stale'
                ? 'Reset stale invitations (dry-run)'
                : 'Backfill dry-run'}
            </DialogTitle>
          </DialogHeader>
          {dialog?.kind === 'reset_stale' ? (
            <div className="text-sm space-y-3"
                  data-testid="debug-reset-stale-dialog-body">
              <div className="text-xs text-neutral-500">
                event_id / campaign_id
              </div>
              <div className="font-mono text-primary-900 text-xs">
                {dialog.eventId}
                <br />
                {dialog.data?.campaign_id}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-neutral-200 rounded-md p-3 bg-red-50">
                  <div className="text-xs text-neutral-500">
                    Would invalidate
                  </div>
                  <div className="text-2xl font-bold text-red-800"
                        data-testid="debug-reset-would-invalidate">
                    {dialog.data?.would_invalidate ?? 0}
                  </div>
                </div>
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    Reissued preserved
                  </div>
                  <div className="text-2xl font-bold text-primary-900"
                        data-testid="debug-reset-reissued-count">
                    {dialog.data?.reissued_count ?? 0}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-neutral-500 mb-1">
                  Reissued registrations preserved
                </div>
                <ul className="text-xs font-mono space-y-0.5 max-h-32 overflow-y-auto">
                  {(dialog.data?.reissued_regs_preserved || []).map((r) => (
                    <li key={r.registration_id}>
                      {r.email_redacted}
                    </li>
                  ))}
                  {!(dialog.data?.reissued_regs_preserved || []).length ? (
                    <li className="text-neutral-500">
                      (none - reset would touch every live row)
                    </li>
                  ) : null}
                </ul>
              </div>
              <div className="text-xs text-neutral-600 border-t pt-2">
                Apply stamps <code>invalidated_at</code> and{' '}
                <code>invalidated_reason=
                post_bundle_110_dedupe_stale_reset</code> on the
                target rows. Reissued recipients (their existing
                valid tokens still work) are never touched.
                Idempotent.
              </div>
            </div>
          ) : dialog?.kind === 'dedupe' ? (
            <div className="text-sm space-y-3"
                  data-testid="debug-dedupe-dialog-body">
              <div className="text-xs text-neutral-500">
                event_id / campaign_id
              </div>
              <div className="font-mono text-primary-900 text-xs">
                {dialog.eventId}
                <br />
                {dialog.campaignId}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    invitations
                  </div>
                  <div className="text-2xl font-bold text-primary-900">
                    {dialog.data?.invitations_total ?? 0}
                  </div>
                </div>
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    unique regs
                  </div>
                  <div className="text-2xl font-bold text-primary-900">
                    {dialog.data?.unique_registrations_inv ?? 0}
                  </div>
                </div>
                <div className="border border-neutral-200 rounded-md p-3 bg-red-50">
                  <div className="text-xs text-neutral-500">
                    to dedupe
                  </div>
                  <div className="text-2xl font-bold text-red-800"
                        data-testid="debug-dedupe-count">
                    {dialog.data?.invitations_to_dedupe ?? 0}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-neutral-500 mb-1">
                  Duplicate-group size histogram
                </div>
                <div className="text-xs font-mono">
                  {Object.entries(dialog.data?.inv_group_size_histogram || {})
                    .sort((a, b) => Number(a[0]) - Number(b[0]))
                    .map(([sz, n]) => `size ${sz}: ${n} groups`)
                    .join('  |  ') || 'no duplicates'}
                </div>
              </div>
              <div className="text-xs text-neutral-600 border-t pt-2">
                Apply will stamp the losing rows with{' '}
                <code>deduplicated_at</code> +{' '}
                <code>invalidated_at</code>. No deletions.
                Idempotent. After apply, Send Live will only count
                one invitation per registration_id.
              </div>
            </div>
          ) : dialog?.kind === 'consolidate' ? (
            <div className="text-sm space-y-3"
                  data-testid="debug-consolidate-dialog-body">
              <div className="text-xs text-neutral-500">event_id</div>
              <div className="font-mono text-primary-900">
                {dialog.eventId}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    KEEP (oldest)
                  </div>
                  <div className="font-mono text-primary-900"
                        data-testid="debug-consolidate-keep">
                    {String(dialog.data?.kept_campaign_id_short || '.')}
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-1">
                    created_at{' '}
                    {(dialog.data?.kept_created_at || '.').slice(0, 19)}
                  </div>
                </div>
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    Will archive
                  </div>
                  <div className="text-2xl font-bold text-red-800"
                        data-testid="debug-consolidate-archive-count">
                    {dialog.data?.archive_candidates?.length ?? 0}
                  </div>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-neutral-500 mb-1">
                  Archive candidates
                </div>
                <ul className="text-xs font-mono space-y-0.5">
                  {(dialog.data?.archive_candidates || []).map((c) => (
                    <li key={c.campaign_id}>
                      {c.campaign_id_short} created_at{' '}
                      {(c.created_at || '.').slice(0, 19)}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="text-xs uppercase text-neutral-500 mb-1">
                  Re-point estimates
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="border border-neutral-200 rounded p-2">
                    invitations{' '}
                    <b>{dialog.data?.repoint_estimates?.invitations ?? 0}</b>
                  </div>
                  <div className="border border-neutral-200 rounded p-2">
                    responses{' '}
                    <b>{dialog.data?.repoint_estimates?.responses ?? 0}</b>
                  </div>
                  <div className="border border-neutral-200 rounded p-2">
                    email_logs{' '}
                    <b>{dialog.data?.repoint_estimates?.email_logs ?? 0}</b>
                  </div>
                </div>
              </div>
              <div className="text-xs text-neutral-600 border-t pt-2">
                Apply will archive the duplicate rows (no delete),
                re-point every child row onto the KEEP campaign, and
                stamp <code>archived_reason=duplicate_of_...</code>.
                Idempotent.
              </div>
            </div>
          ) : (
            <div className="text-sm space-y-3">
              <div className="text-xs text-neutral-500">
                event_id
              </div>
              <div className="font-mono text-primary-900">
                {dialog?.eventId}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    to mint
                  </div>
                  <div className="text-2xl font-bold text-primary-900"
                        data-testid="debug-dryrun-to-mint">
                    {dialog?.data?.to_mint_count ?? 0}
                  </div>
                </div>
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    already fine
                  </div>
                  <div className="text-2xl font-bold text-neutral-800">
                    {dialog?.data?.already_fine_count ?? 0}
                  </div>
                </div>
                <div className="border border-neutral-200 rounded-md p-3">
                  <div className="text-xs text-neutral-500">
                    skipped
                  </div>
                  <div className="text-2xl font-bold text-neutral-800">
                    {dialog?.data?.skipped_missing_inv_count ?? 0}
                  </div>
                </div>
              </div>
              <div className="text-xs text-neutral-500">
                Target campaign
              </div>
              <div className="font-mono text-primary-900">
                {dialog?.data?.campaign_id || '.'}
              </div>
              <div className="text-xs text-neutral-600 border-t pt-2">
                This was a dry-run. No rows were minted. Screenshot
                these numbers to the agent for the safe apply plan.
              </div>
            </div>
          )}
          <DialogFooter>
            {dialog?.kind === 'consolidate' ? (
              <Button className="bg-red-800 text-white hover:bg-red-700"
                        onClick={() => applyConsolidate(dialog.eventId)}
                        disabled={busy || !(dialog.data?.archive_candidates?.length)}
                        data-testid="debug-consolidate-apply-btn">
                Apply consolidation
              </Button>
            ) : null}
            {dialog?.kind === 'dedupe' ? (
              <Button className="bg-red-800 text-white hover:bg-red-700"
                        onClick={() => applyDedupe(dialog.eventId, dialog.campaignId)}
                        disabled={busy || !(dialog.data?.invitations_to_dedupe)}
                        data-testid="debug-dedupe-apply-btn">
                Apply dedupe
              </Button>
            ) : null}
            {dialog?.kind === 'reset_stale' ? (
              <Button className="bg-red-800 text-white hover:bg-red-700"
                        onClick={() => applyResetStale(dialog.eventId)}
                        disabled={busy || !(dialog.data?.would_invalidate)}
                        data-testid="debug-reset-stale-apply-btn">
                Apply reset
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setDialog(null)}
                      data-testid="debug-dryrun-close-btn">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDebugCampaignsPage;
